import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loginUser } from '../store';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  Sparkles,
  Activity,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import AuthSplitLayout from './AuthSplitLayout';
import { normalizeApiError } from '../lib/i18n';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error } = useSelector((state) => state.auth);
  const { t } = useTranslation();

  const infoMessage = useMemo(() => {
    if (location.state?.message) {
      return location.state.message;
    }

    const params = new URLSearchParams(location.search);
    if (params.get('expired') === 'true') {
      return t('auth.login.expired');
    }

    return '';
  }, [location.search, location.state, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(loginUser({ email, password }));
    if (loginUser.fulfilled.match(result)) {
      navigate('/dashboard');
    }
  };

  return (
    <AuthSplitLayout
      badge={t('auth.login.badge')}
      title={t('auth.login.title')}
      description={t('auth.login.description')}
      dynamicPreview
      sideLabel={t('auth.login.sideLabel')}
      sideTitle={t('auth.login.sideTitle')}
      sideDescription={t('auth.login.sideDescription')}
      stats={[
        { value: '24/7', label: t('auth.login.workspaceAccess') },
        { value: '99.9%', label: t('auth.login.sessionStability') },
        { value: '1 hub', label: t('auth.login.campaignControl') },
      ]}
      highlights={[
        {
          icon: ShieldCheck,
          title: t('auth.login.protectedSessionTitle'),
          description: t('auth.login.protectedSessionDescription'),
        },
        {
          icon: Sparkles,
          title: t('auth.login.cleanerExperienceTitle'),
          description: t('auth.login.cleanerExperienceDescription'),
        },
        {
          icon: Activity,
          title: t('auth.login.activeTeamsTitle'),
          description: t('auth.login.activeTeamsDescription'),
        },
      ]}
      quote={t('auth.login.quote')}
      quoteAuthor={t('auth.login.quoteAuthor')}
      quoteRole={t('auth.login.quoteRole')}
    >
      <div className="rounded-[26px] border border-slate-200/80 bg-white/92 p-5 shadow-[0_20px_40px_rgba(15,23,42,0.06)] lg:p-4 xl:p-6">
        <div className="mb-5 flex items-start justify-between gap-4 lg:mb-4">
          <div>
            <p className="text-sm font-semibold text-slate-950">{t('auth.login.cardTitle')}</p>
            <p className="mt-1 text-sm text-slate-500">{t('auth.login.cardDescription')}</p>
          </div>
          <div className="rounded-2xl bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600 lg:px-2 lg:py-1">
            {t('auth.login.fastAccess')}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 lg:gap-3">
          <div className="grid gap-2 lg:gap-1">
            <Label htmlFor="email" className="text-slate-700 text-xs sm:text-sm lg:text-xs">{t('auth.login.email')}</Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="h-12 rounded-2xl border-slate-200 bg-slate-50 pl-11 pr-4 text-slate-950 placeholder:text-slate-400 focus-visible:ring-sky-500 lg:h-10 lg:text-sm"
              />
            </div>
          </div>

          <div className="grid gap-2 lg:gap-1">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="password" className="text-slate-700 text-xs sm:text-sm lg:text-xs">{t('auth.login.password')}</Label>
              <span className="text-xs font-medium text-slate-400">{t('auth.login.encryptedSession')}</span>
            </div>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="h-12 rounded-2xl border-slate-200 bg-slate-50 pl-11 pr-12 text-slate-950 focus-visible:ring-sky-500 lg:h-10 lg:text-sm"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-700"
                aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {infoMessage && (
            <Alert className="border-sky-200 bg-sky-50 text-sky-900">
              <AlertDescription>{infoMessage}</AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive" className="border-red-200 bg-red-50 text-red-900">
              <AlertDescription>{normalizeApiError(error, t)}</AlertDescription>
            </Alert>
          )}

          <Button type="submit" className="h-12 w-full rounded-2xl bg-slate-950 font-semibold text-white shadow-[0_18px_35px_rgba(15,23,42,0.18)] transition-all hover:bg-slate-800 lg:h-10 lg:text-sm" disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading ? t('auth.login.submitting') : t('auth.login.submit')}
          </Button>
        </form>

        <div className="mt-6 rounded-2xl border border-slate-100 bg-slate-50/50 p-4 lg:mt-5 lg:p-3">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            {t('auth.login.demoAccount')}
          </p>
          <div className="grid gap-1 sm:grid-cols-2 sm:gap-4">
            <div className="flex flex-col">
              <span className="text-[10px] text-slate-400">{t('auth.login.email')}</span>
              <span className="text-xs font-medium text-slate-900 sm:text-sm">{t('auth.login.demoEmail')}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] text-slate-400">{t('auth.login.password')}</span>
              <span className="text-xs font-medium text-slate-900 sm:text-sm">{t('auth.login.demoPassword')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 text-sm text-slate-600 lg:gap-2">
        <p>
          {t('auth.login.noAccount')}{' '}
          <Link to="/register" className="font-semibold text-slate-950 transition-colors hover:text-sky-700">
            {t('actions.createOne')}
          </Link>
        </p>
        <p>
          <Link to="/forgot-password" className="font-medium text-slate-500 transition-colors hover:text-slate-950">
            Şifrenizi mi unuttunuz? / Forgot Password?
          </Link>
        </p>
      </div>
    </AuthSplitLayout>
  );
}
